import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {
  confusionMatrixCellIdQueryParam,
  evaluationCellPredictedClassQueryParam,
  evaluationPipelineIdQueryParam,
  finetuneMetricsInterpretationQueryParam,
  imageBrowserAreaSizeQueryParam,
  imageBrowserConfidenceThresholdNotRoundedQueryParam,
  imageBrowserDatasetIdQueryParam,
  imageBrowserHumanLabelQueryParam,
  imageBrowserMachineLabelQueryParam,
  imageBrowserPageQueryParam,
  keypointFinetuneQueryParam,
  similaritySearchQueryParam,
  useQueryParam,
} from '~utils/routeUtil';
import {useIsUnstableGallery} from '~hooks/useIsUnstableGallery';
import {ImageFilters, ImageOrderBy, LabelItem, PaginationActionName} from '~redux/types/images';
import {annotatorActions} from '~redux/reducers/annotatorReducer';
import {
  imageDataActions,
  selectDeletedImagesCount,
  selectImages,
  selectImagesActualTotalCount,
  selectImagesTotalCount,
  selectPageNumber,
  selectPaginationActions,
} from '~redux/reducers/imageReducer';
import {selectCurrentProjectId} from '~redux/reducers/userReducer';
import {
  bulkClassifyImages,
  bulkUpdateMeta,
  deleteImages,
  FetchImagesArgs,
  updateImagesData,
} from '~redux/actions/imageActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {
  parseSimSearchFilter,
  useImageFilters,
  UseImageFiltersOptions,
} from '~components/images/image-overview/imageOverview.hooks';
import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';
import {useCombinedCheckedItems} from 'src/contexts/checked-items/CombinedCheckedItemsContext';
import {useImagesCache} from 'src/contexts/ImagesCacheContext';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {useAnnotationTelemetry} from './useAnnotationTelemetry';
import {useAnnotationUIContext} from './useAnnotationUIContext';

export const useConfusionCellFromQueryParams = (
  newConfusionMatrixCellId?: string,
): ConfusionCell | ConfusionCell[] | undefined => {
  const [confusionMatrixCellId] = useQueryParam(confusionMatrixCellIdQueryParam);
  const [activeCellHumanLabel] = useQueryParam(imageBrowserHumanLabelQueryParam);
  const [activeCellMachineLabel] = useQueryParam(imageBrowserMachineLabelQueryParam);
  const [imageBrowserAreaSize] = useQueryParam(imageBrowserAreaSizeQueryParam);
  const [imageBrowserConfidenceThreshold] = useQueryParam(imageBrowserConfidenceThresholdNotRoundedQueryParam);
  const [imageBrowserDatasetId] = useQueryParam(imageBrowserDatasetIdQueryParam);
  const [finetuneMetricsInterpretation] = useQueryParam(finetuneMetricsInterpretationQueryParam);
  const areaSize = imageBrowserAreaSize ? parseInt(imageBrowserAreaSize as string) : undefined;
  const confidence = imageBrowserConfidenceThreshold
    ? parseFloat(imageBrowserConfidenceThreshold as string)
    : undefined;
  const rawCellId = newConfusionMatrixCellId ?? confusionMatrixCellId;
  const activeConfusionMatrixCellId = Array.isArray(rawCellId) ? rawCellId.join('|') : (rawCellId as string);
  const pipelineId = activeConfusionMatrixCellId?.split('_')[0];

  if (!activeConfusionMatrixCellId || areaSize === undefined || confidence === undefined || !pipelineId) {
    return;
  }

  // When humanLabel and machineLabel are arrays (pipe-separated in URL), return multiple confusion cells.
  // This is used for accuracy "view images" which needs all diagonal cells (all correctly classified images).
  const humanLabels = Array.isArray(activeCellHumanLabel) ? activeCellHumanLabel : [activeCellHumanLabel as string];
  const machineLabels = Array.isArray(activeCellMachineLabel)
    ? activeCellMachineLabel
    : [activeCellMachineLabel as string];

  if (humanLabels.length > 1 && humanLabels.length === machineLabels.length) {
    return humanLabels.map((humanLabel, index) => ({
      actualClass: humanLabel as string,
      predictedClass: machineLabels[index] as string,
      areaSize,
      confidence,
      datasetId: imageBrowserDatasetId as Dataset,
      pipelineId,
      interpretation: finetuneMetricsInterpretation as Interpretation,
    }));
  }

  // Without both labels there is no valid single confusion cell; returning a partial cell would produce a
  // malformed backend request (missing actualClass/predictedClass), so bail out instead.
  if (!humanLabels[0] || !machineLabels[0]) {
    return;
  }

  return {
    actualClass: humanLabels[0] as string,
    predictedClass: machineLabels[0] as string,
    areaSize,
    confidence,
    datasetId: imageBrowserDatasetId as Dataset,
    pipelineId,
    interpretation: finetuneMetricsInterpretation as Interpretation,
  };
};

export const useEvaluationCellFromQueryParams = (): EvaluationCell | undefined => {
  const [evaluationPipelineId] = useQueryParam(evaluationPipelineIdQueryParam);
  const [imageBrowserAreaSize] = useQueryParam(imageBrowserAreaSizeQueryParam);
  const [imageBrowserConfidenceThreshold] = useQueryParam(imageBrowserConfidenceThresholdNotRoundedQueryParam);
  const [evaluationCellPredictedClass] = useQueryParam(evaluationCellPredictedClassQueryParam);

  const pipelineId = evaluationPipelineId as string;
  const areaSize = imageBrowserAreaSize ? parseInt(imageBrowserAreaSize as string) : undefined;
  const confidence = imageBrowserConfidenceThreshold
    ? parseFloat(imageBrowserConfidenceThreshold as string)
    : undefined;

  if (!pipelineId || areaSize === undefined || confidence === undefined) {
    return;
  }

  return {
    pipelineId,
    areaSize,
    confidence,
    predictedClass: evaluationCellPredictedClass as string,
  };
};

export const useSimilaritySearchFromQueryParams = (): SimSearchFilter | undefined => {
  const [similaritySearch] = useQueryParam(similaritySearchQueryParam);
  if (!similaritySearch) {
    return;
  }

  const parsedSimSearch = parseSimSearchFilter(similaritySearch as string);
  if (!parsedSimSearch) {
    return;
  }

  const {imageId, searchCoordinate, count} = parsedSimSearch;
  return {
    imageId,
    searchCoordinate,
    count,
  };
};

export const useKeypointFinetuneFromQueryParams = (): [string | undefined, KeypointFinetune | undefined] => {
  const [keypointFinetune] = useQueryParam(keypointFinetuneQueryParam);
  const parsedKeypointFinetune = useMemo(() => {
    if (!keypointFinetune) {
      return undefined;
    }

    // Parse the keypointFinetune string back to KeypointFinetune object
    // Using the same parsing logic as in imageOverview.hooks.ts
    const [
      datasetId,
      pipelineId,
      keypointLabelId,
      threshold,
      minDistance,
      maxDistance,
      selectedDistance,
      withinSelectedDistance,
      outsideSelectedDistance,
      totalDistances,
    ] = (keypointFinetune as string).split('-');

    return {
      datasetId: datasetId as Dataset,
      pipelineId,
      keypointLabelId,
      threshold: threshold as KeypointThreshold,
      minDistance: minDistance ? Number(minDistance) : null,
      maxDistance: maxDistance ? Number(maxDistance) : null,
      selectedDistance: selectedDistance ? Number(selectedDistance) : null,
      withinSelectedDistance: withinSelectedDistance ? Number(withinSelectedDistance) : null,
      outsideSelectedDistance: outsideSelectedDistance ? Number(outsideSelectedDistance) : null,
      totalDistances: Number(totalDistances),
    };
  }, [keypointFinetune]);

  return [keypointFinetune as string | undefined, parsedKeypointFinetune];
};

export type UseImagesOverviewOptions = UseImageFiltersOptions;

export function useImagesOverview(
  mode: ImageOverviewMode,
  currentLabels: LabelItem[] | null,
  activeConfusionMatrixCellId?: string,
  enabled: boolean = true,
  options?: UseImagesOverviewOptions,
) {
  const dispatch = useAppDispatch();
  const images = useAppSelector(selectImages);
  const imageCount = useAppSelector(selectImagesTotalCount);
  const actualImageCount = useAppSelector(selectImagesActualTotalCount);
  const deletedImagesCount = useAppSelector(selectDeletedImagesCount);
  const availablePaginationActions = useAppSelector(selectPaginationActions);
  const currentPage = useAppSelector(selectPageNumber);
  const {currentModel} = useCurrentModel();
  const confusionCell = useConfusionCellFromQueryParams(activeConfusionMatrixCellId);
  const evaluationCell = useEvaluationCellFromQueryParams();
  const withUnstablePredictions = useIsUnstableGallery() || undefined;
  const similaritySearch = useSimilaritySearchFromQueryParams();
  const [keypointFinetuneString, keypointFinetune] = useKeypointFinetuneFromQueryParams();
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const uiContext = useAnnotationUIContext();
  const telemetry = useAnnotationTelemetry();
  const {loadImagesFromCache} = useImagesCache();
  const [imageFilters, updateImageFilters] = useImageFilters(mode, currentLabels, enabled, options);
  const {imageCheckedItemsState} = useCombinedCheckedItems();

  const {checkedItemIds: checkedImagesIds, setAllChecked} = imageCheckedItemsState ?? {
    checkedItemIds: [],
    setAllChecked: () => {},
  };

  const [isLoading, setIsLoading] = useState<boolean | undefined>(undefined);

  const isPageChangeInFlightRef = useRef(false);
  const [isPageChangeInFlight, setIsPageChangeInFlight] = useState(false);

  const [imageBrowserPageParam, setImageBrowserPageParam] = useQueryParam(imageBrowserPageQueryParam);

  const [initialUrlPage] = useState(() => {
    return imageBrowserPageParam ? parseInt(imageBrowserPageParam as string, 10) : 1;
  });

  const updateImages = async (options?: FetchImagesArgs) => {
    if (!enabled) {
      return;
    }

    const {orderBy, ...filtersWithoutPagination} = imageFilters || {};

    await dispatch(
      updateImagesData({
        withGlobalLoading: true,
        imageFilters: {
          ...filtersWithoutPagination,
          orderBy,
          confusionCell,
          evaluationCell,
          withUnstablePredictions,
          similaritySearch,
          keypointFinetune,
          modelId: mode === 'model-defect-book' ? currentModel?.id : undefined,
        },
        ...options,
        paginationActionWithTargetPage: options?.paginationActionWithTargetPage,
      }),
    );
    dispatch(imageDataActions.resetOverviewPageNumber());
  };

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const fetchDataAndResetToFirstPage = async () => {
      setIsLoading(true);
      dispatch(imageDataActions.resetImageIndex());
      await updateImages();
      dispatch(imageDataActions.resetOverviewPageNumber());
      setIsLoading(false);
    };

    const refetchDataForCurrentPage = async (pageOverride?: number) => {
      if (!enabled) {
        return;
      }

      const effectivePage = pageOverride ?? currentPage;

      // Update data of the current page in the background, so that the user can continue working with the cached data.
      // Enable diff check to only update the data if it has changed.
      const cursor = availablePaginationActions?.jump?.cursor;
      if (!cursor) {
        // cannot refetch current page without a cursor, so we need to fetch all data
        // and reset the page number to 1
        fetchDataAndResetToFirstPage();
        return;
      }

      let cursorCurationScore: number | undefined;
      const isCurationScoreSort = imageFilters?.orderBy?.startsWith('CURATION_SCORE');
      if (isCurationScoreSort && cursor && images.length > 0) {
        const cursorImage = images.find((img) => img.id === cursor);
        if (cursorImage?.curationScore != null) {
          cursorCurationScore = cursorImage.curationScore;
        }
      }

      const jumpToCurrentPage = {
        action: {
          name: PaginationActionName.JUMP,
          cursor,
          ...(cursorCurationScore !== undefined && {cursorCurationScore}),
        },
        targetPage: effectivePage,
      };

      await dispatch(
        updateImagesData({
          imageFilters: {
            ...imageFilters,
            confusionCell,
            evaluationCell,
            withUnstablePredictions,
            similaritySearch,
            keypointFinetune,
            modelId: mode === 'model-defect-book' ? currentModel?.id : undefined,
          },
          withDiffCheck: true,
          paginationActionWithTargetPage: jumpToCurrentPage,
          bustCache: true,
        }),
      );
    };

    const shouldRestoreCurrentPage =
      (loadImagesFromCache && mode === 'model-defect-book') ||
      (mode === 'image-browser' && initialUrlPage > 1 && availablePaginationActions?.jump?.cursor);

    const shouldFetchFreshData =
      (!loadImagesFromCache && mode === 'model-defect-book') ||
      (mode === 'image-browser' &&
        (activeConfusionMatrixCellId || keypointFinetuneString || withUnstablePredictions) &&
        (initialUrlPage === 1 || !availablePaginationActions?.jump?.cursor));

    if (shouldRestoreCurrentPage) {
      if (mode === 'image-browser' && initialUrlPage > 1 && initialUrlPage !== currentPage) {
        dispatch(imageDataActions.setOverviewPageNumber(initialUrlPage));
      }
      refetchDataForCurrentPage(mode === 'image-browser' ? initialUrlPage : undefined);
    } else if (shouldFetchFreshData) {
      fetchDataAndResetToFirstPage();
      if (mode === 'image-browser') {
        setImageBrowserPageParam(undefined);
      }
    }

    // This should only run once unless the confusion matrix cell / keypoint / unstable-gallery entry point changes.
    // data is only retrieved once, either from the Redux cache or by fetching it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeConfusionMatrixCellId,
    keypointFinetuneString,
    withUnstablePredictions,
    currentProjectId,
    currentModel?.id,
    enabled,
  ]);

  const handlePageChange = async (action: PaginationActionName, targetPage: number) => {
    if (isPageChangeInFlightRef.current) {
      return;
    }
    const paginationAction = availablePaginationActions ? availablePaginationActions[action] : undefined;
    if (!paginationAction) {
      // No cursors means there is no reference point to page from: the list request for the current
      // filters never landed (it failed, or the response carried no actions), while the previous page and
      // its total stayed on screen. Load the list fresh instead of leaving the controls inert - the
      // response re-establishes the cursors, and updateImages resets to the first page.
      isPageChangeInFlightRef.current = true;
      setIsPageChangeInFlight(true);
      try {
        await updateImages();
      } finally {
        isPageChangeInFlightRef.current = false;
        setIsPageChangeInFlight(false);
      }
      if (mode === 'image-browser') {
        setImageBrowserPageParam(undefined);
      }
      return;
    }

    const pageOffset = action === PaginationActionName.JUMP ? targetPage - currentPage : undefined;

    let cursorCurationScore: number | undefined;
    const isCurationScoreSort = imageFilters?.orderBy?.startsWith('CURATION_SCORE');
    if (isCurationScoreSort && paginationAction.cursor && images.length > 0) {
      const cursorImage = images.find((img) => img.id === paginationAction.cursor);
      if (cursorImage?.curationScore != null) {
        cursorCurationScore = cursorImage.curationScore;
      }
    }

    isPageChangeInFlightRef.current = true;
    setIsPageChangeInFlight(true);
    dispatch(imageDataActions.setOverviewPageNumber(targetPage));
    if (mode === 'image-browser') {
      setImageBrowserPageParam(targetPage > 1 ? targetPage.toString() : undefined);
    }
    try {
      await updateImageFilters(
        {},
        {
          action: {...paginationAction, pageOffset, ...(cursorCurationScore !== undefined && {cursorCurationScore})},
          targetPage,
        },
      );
    } finally {
      isPageChangeInFlightRef.current = false;
      setIsPageChangeInFlight(false);
    }
  };

  const handleFilterChange = async (newFilters: Partial<ImageFilters>) => {
    if ('trainingTag' in newFilters) {
      // When a dataset version is selected, the image cannot be modified because
      // a dataset is a "frozen" representation of a certain point in time
      dispatch(annotatorActions.setImageReadOnly(newFilters.trainingTag !== undefined));
    }

    if ('automlPipelineId' in newFilters) {
      // When a snapshot version is selected, the image cannot be modified because
      // a snapshot is a "frozen" representation of a certain point in time
      dispatch(annotatorActions.setImageReadOnly(newFilters.automlPipelineId !== undefined));
    }

    // The cursors in redux belong to the previous filter set. Clearing them before the refetch keeps a
    // page change issued while the request is still in flight from sending a cursor the new filters
    // exclude, which the backend rejects with 400 "Cursor <id> does not exist in collection.".
    dispatch(imageDataActions.clearPaginationActions());
    dispatch(imageDataActions.resetOverviewPageNumber());
    if (mode === 'image-browser') {
      setImageBrowserPageParam(undefined);
    }

    await updateImageFilters(newFilters);
  };

  const handleBatchDelete = async () => {
    await dispatch(deleteImages(checkedImagesIds));
    await updateImages();

    setAllChecked(false);
  };

  const handleBatchAnnotationDelete = async (context: string) => {
    const payload = {
      filters: {
        objectIds: checkedImagesIds,
      },
      uiContext: context,
      labelsByModel: {
        [currentModel!.id]: [],
      },
    };
    await dispatch(bulkClassifyImages(payload));
    await updateImages();
    setAllChecked(false);
  };

  const handleBatchProductTypeChange = async (productType: string) => {
    const payload = {
      filters: {
        objectIds: checkedImagesIds,
      },
      meta: {
        product_type: productType,
      },
    };
    await dispatch(bulkUpdateMeta(payload));
    await updateImages();
  };

  const handleBatchPerspectiveChange = async (perspective: number) => {
    const payload = {
      filters: {
        objectIds: checkedImagesIds,
      },
      meta: {
        perspective,
      },
    };
    await dispatch(bulkUpdateMeta(payload));
    await updateImages();
  };

  const handleBatchClassLabelChange = async (labelItem: LabelItem, modelId: string) => {
    const payload = {
      uiContext: `${uiContext}-batch-edit`,
      telemetry,
      filters: {
        objectIds: checkedImagesIds,
      },
      labelsByModel: {
        [modelId]: [{id: labelItem.id, type: labelItem.type}],
      },
    };
    await dispatch(bulkClassifyImages(payload));
    await updateImages();
  };

  const handleImageOrderChange = useCallback(
    (orderBy: ImageOrderBy) => {
      // A cursor encodes a position in the ordering it was minted under, so paging with it after the sort
      // changed returns a page that is not the requested one. Drop them for the same reason as in
      // handleFilterChange.
      dispatch(imageDataActions.clearPaginationActions());
      updateImageFilters({orderBy});
      dispatch(imageDataActions.resetOverviewPageNumber());
      if (mode === 'image-browser') {
        setImageBrowserPageParam(undefined);
      }
    },
    [dispatch, updateImageFilters, mode, setImageBrowserPageParam],
  );

  return {
    images,
    imageCheckedItemsState,
    imageCount,
    actualImageCount,
    deletedImagesCount,
    imageFilters,
    currentPage,
    handlePageChange,
    isPageChangeInFlight,
    updateImages,
    isLoading,
    handleFilterChange,
    handleBatchDelete,
    handleBatchAnnotationDelete,
    handleBatchProductTypeChange,
    handleBatchClassLabelChange,
    handleBatchPerspectiveChange,
    handleImageOrderChange,
  };
}
